import * as React from "react";
import { cn } from "@/lib/utils";
import { useNativeSheetDismissContext } from "@/lib/native/nativeSheetDismissContext";
import {
  countOpenNativeSheets,
  nativeSheetExpandPreference,
  syncNativeSheetExpandState,
} from "@/lib/native/nativeSheetExpandPreference";

const DISMISS_THRESHOLD_PX = 64;
const DISMISS_THRESHOLD_EXPANDED_PX = 88;
const EXPAND_THRESHOLD_PX = 48;
const COLLAPSE_THRESHOLD_PX = 56;
const DISMISS_ANIM_MS = 340;
const SNAP_BACK_MS = 220;

type UseNativeSheetSwipeOptions = {
  enabled?: boolean;
  /** Swipe-down anywhere on the sheet (fullscreen lightbox). */
  dismissFromFullSheet?: boolean;
  /** Allow swipe up to expand sheet height (70dvh → near full screen). */
  expandable?: boolean;
  /**
   * When already expanded, pull-down dismisses instead of collapsing first.
   * Use for full-screen flows (För dig annons) where collapse is not useful.
   */
  dismissWhenExpanded?: boolean;
  /** Reset expand/drag state when the sheet closes. */
  sheetOpen?: boolean;
};

function findSheetOverlay(sheetEl: HTMLElement): HTMLElement | null {
  const portal =
    sheetEl.closest("[data-radix-portal]") ??
    sheetEl.parentElement?.parentElement;
  if (!portal) return null;
  return portal.querySelector(
    "[data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]",
  ) as HTMLElement | null;
}

function offScreenDy(): number {
  return typeof window !== "undefined" ? window.innerHeight + 48 : 900;
}

function setSheetMotionState(
  sheet: HTMLElement | null,
  state: "dragging" | "dismissing" | null,
): void {
  if (!sheet) return;
  sheet.classList.remove("native-sheet-dragging", "native-sheet-dismissing");
  if (state === "dragging") sheet.classList.add("native-sheet-dragging");
  if (state === "dismissing") sheet.classList.add("native-sheet-dismissing");
}

function clearSheetDragTransform(sheet: HTMLElement): void {
  sheet.getAnimations().forEach((a) => a.cancel());
  sheet.style.transform = "";
}

function resetSheetExpandState(
  sheet: HTMLElement | null,
  expandedRef: React.MutableRefObject<boolean>,
): void {
  expandedRef.current = false;
  if (!sheet) return;
  sheet.classList.remove(
    "native-sheet-expanded",
    "native-sheet-dragging",
    "native-sheet-dismissing",
  );
  clearSheetDragTransform(sheet);
}

function snapSheetTransformToZero(
  sheet: HTMLElement,
  fromDy: number,
): Promise<void> {
  if (fromDy === 0) {
    sheet.style.transform = "";
    return Promise.resolve();
  }

  const anim = sheet.animate(
    [
      { transform: `translate3d(0, ${fromDy}px, 0)` },
      { transform: "translate3d(0, 0, 0)" },
    ],
    {
      duration: SNAP_BACK_MS,
      easing: "cubic-bezier(0.32, 0.72, 0, 1)",
      fill: "none",
    },
  );

  return anim.finished
    .then(() => {
      sheet.style.transform = "";
    })
    .catch(() => {
      sheet.style.transform = "";
    });
}

/** Commit off-screen state before Radix unmount (WAAPI fill is cleared on re-render). */
function lockSheetOffScreen(sheet: HTMLElement, dy = offScreenDy()): void {
  sheet.getAnimations().forEach((a) => a.cancel());
  sheet.classList.add("native-sheet-dismissing", "native-sheet-exit-locked");
  sheet.style.transform = `translate3d(0, ${dy}px, 0)`;
  sheet.style.opacity = "0";
  sheet.style.pointerEvents = "none";
}

function lockOverlayHidden(overlay: HTMLElement | null): void {
  if (!overlay) return;
  overlay.getAnimations().forEach((a) => a.cancel());
  overlay.classList.add("native-sheet-overlay-dismissing");
  overlay.style.opacity = "0";
  overlay.style.pointerEvents = "none";
}

function finishDismissThenClose(
  sheet: HTMLElement,
  overlay: HTMLElement | null,
  endDy: number,
  onComplete: () => void,
): void {
  lockSheetOffScreen(sheet, endDy);
  lockOverlayHidden(overlay);
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      onComplete();
    });
  });
}

function runSlideDismissAnimation(
  sheet: HTMLElement,
  fromDy: number,
  onComplete: () => void,
): void {
  const overlay = findSheetOverlay(sheet);
  const startDy = Math.max(0, fromDy);
  const endDy = offScreenDy();

  setSheetMotionState(sheet, "dismissing");
  overlay?.classList.add("native-sheet-overlay-dismissing");

  sheet.style.transform = `translate3d(0, ${startDy}px, 0)`;

  const easing = "cubic-bezier(0.32, 0.72, 0, 1)";
  const sheetAnim = sheet.animate(
    [
      { transform: `translate3d(0, ${startDy}px, 0)` },
      { transform: `translate3d(0, ${endDy}px, 0)` },
    ],
    { duration: DISMISS_ANIM_MS, easing, fill: "forwards" },
  );

  const overlayAnim =
    overlay &&
    overlay.animate([{ opacity: 1 }, { opacity: 0 }], {
      duration: DISMISS_ANIM_MS,
      easing: "ease-out",
      fill: "forwards",
    });

  void Promise.all([
    sheetAnim.finished.catch(() => undefined),
    overlayAnim?.finished.catch(() => undefined),
  ]).then(() => {
    finishDismissThenClose(sheet, overlay, endDy, onComplete);
  });
}

function runExpandHeightAnimation(sheet: HTMLElement): void {
  sheet.classList.add("native-sheet-expand-animating");
  window.setTimeout(() => {
    sheet.classList.remove("native-sheet-expand-animating");
  }, 340);
}

/**
 * Swipe-down to dismiss native bottom sheets.
 * Transform is applied on the Radix content (`.native-bottom-sheet`) so the whole sheet moves as one.
 */
export function useNativeSheetSwipe({
  enabled = true,
  dismissFromFullSheet = false,
  expandable = false,
  dismissWhenExpanded = false,
  sheetOpen = true,
}: UseNativeSheetSwipeOptions = {}) {
  const dismissCtx = useNativeSheetDismissContext();
  const sheetRef = React.useRef<HTMLElement | null>(null);
  const startY = React.useRef(0);
  const dragging = React.useRef(false);
  const dismissing = React.useRef(false);
  const expandedRef = React.useRef(false);

  const setExpanded = React.useCallback((next: boolean, fromDy = 0) => {
    const sheet = sheetRef.current;
    expandedRef.current = next;
    nativeSheetExpandPreference.set(next);
    if (!sheet) return;

    clearSheetDragTransform(sheet);
    runExpandHeightAnimation(sheet);

    if (next) {
      sheet.classList.add("native-sheet-expanded");
      void snapSheetTransformToZero(sheet, Math.min(0, fromDy)).then(() => {
        if (sheetRef.current === sheet) {
          setSheetMotionState(sheet, null);
        }
      });
      return;
    }

    sheet.classList.remove("native-sheet-expanded");
    const startDy = fromDy > 0 ? fromDy : 0;
    void snapSheetTransformToZero(sheet, startDy).then(() => {
      if (sheetRef.current === sheet) {
        setSheetMotionState(sheet, null);
      }
    });
  }, []);

  const mergeSheetRef = React.useCallback(
    (externalRef: React.ForwardedRef<HTMLElement>) =>
      (node: HTMLElement | null) => {
        if (!node) {
          dragging.current = false;
          dismissing.current = false;
          expandedRef.current = false;
          sheetRef.current = null;
        } else if (node !== sheetRef.current) {
          if (expandable && nativeSheetExpandPreference.get()) {
            expandedRef.current = true;
            node.classList.add("native-sheet-expanded");
            clearSheetDragTransform(node);
          } else {
            resetSheetExpandState(node, expandedRef);
          }
          dragging.current = false;
          dismissing.current = false;
        }

        sheetRef.current = node;
        if (
          node &&
          expandable &&
          sheetOpen &&
          nativeSheetExpandPreference.get() &&
          !expandedRef.current
        ) {
          expandedRef.current = true;
          node.classList.add("native-sheet-expanded");
        }
        if (typeof externalRef === "function") externalRef(node);
        else if (externalRef) {
          (externalRef as React.MutableRefObject<HTMLElement | null>).current =
            node;
        }
      },
    [expandable, sheetOpen],
  );

  const runDismiss = React.useCallback(
    (fromDy: number) => {
      const sheet = sheetRef.current;
      if (!sheet || dismissing.current) return false;
      dismissing.current = true;
      dragging.current = false;
      resetSheetExpandState(sheet, expandedRef);

      runSlideDismissAnimation(sheet, fromDy, () => {
        dismissCtx?.requestClose();
        dismissing.current = false;
      });
      return true;
    },
    [dismissCtx],
  );

  React.useEffect(() => {
    if (!enabled || !dismissCtx) return;
    dismissCtx.registerDismissAnimator((onComplete, fromDy = 0) => {
      const sheet = sheetRef.current;
      if (!sheet || dismissing.current) return false;
      dismissing.current = true;
      resetSheetExpandState(sheet, expandedRef);
      runSlideDismissAnimation(sheet, fromDy, () => {
        dismissing.current = false;
        onComplete();
      });
      return true;
    });
    return () => dismissCtx.registerDismissAnimator(null);
  }, [enabled, dismissCtx]);

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled || dismissing.current || e.button !== 0) return;
      dragging.current = true;
      startY.current = e.clientY;
      e.currentTarget.setPointerCapture(e.pointerId);
      setSheetMotionState(sheetRef.current, "dragging");
      e.preventDefault();
      e.stopPropagation();
    },
    [enabled],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled || !dragging.current || dismissing.current) return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const rawDy = e.clientY - startY.current;

      if (expandable) {
        if (!expandedRef.current) {
          sheet.style.transform = `translate3d(0, ${rawDy}px, 0)`;
        } else if (rawDy > 0) {
          sheet.style.transform = `translate3d(0, ${rawDy}px, 0)`;
        } else {
          sheet.style.transform = `translate3d(0, ${Math.max(rawDy, -20)}px, 0)`;
        }
      } else {
        const dy = Math.max(0, rawDy);
        sheet.style.transform = `translate3d(0, ${dy}px, 0)`;
      }

      e.preventDefault();
      e.stopPropagation();
    },
    [enabled, expandable],
  );

  const finishDrag = React.useCallback(
    (clientY: number) => {
      if (!enabled || !dragging.current || dismissing.current) return;
      const sheet = sheetRef.current;
      if (!sheet) return;

      const dy = clientY - startY.current;
      dragging.current = false;

      if (expandable) {
        if (dy <= -EXPAND_THRESHOLD_PX && !expandedRef.current) {
          setExpanded(true, dy);
          return;
        }
        // Full-screen flows (För dig annons): pull down = dismiss, never collapse
        if (expandedRef.current && dismissWhenExpanded) {
          const downDy = Math.max(0, dy);
          if (downDy >= DISMISS_THRESHOLD_EXPANDED_PX) {
            runDismiss(downDy);
          } else {
            void snapSheetTransformToZero(sheet, downDy).then(() => {
              if (sheetRef.current === sheet) {
                setSheetMotionState(sheet, null);
              }
            });
          }
          return;
        }
        if (expandedRef.current && dy >= COLLAPSE_THRESHOLD_PX) {
          const dismissAt = DISMISS_THRESHOLD_EXPANDED_PX;
          if (dy < dismissAt) {
            setExpanded(false, dy);
            return;
          }
        }
      }

      const downDy = Math.max(0, dy);
      const dismissAt = expandable && expandedRef.current
        ? DISMISS_THRESHOLD_EXPANDED_PX
        : DISMISS_THRESHOLD_PX;
      if (downDy >= dismissAt) {
        runDismiss(downDy);
      } else {
        const startTransform = expandable && dy < 0 ? dy : downDy;
        void snapSheetTransformToZero(sheet, startTransform).then(() => {
          if (sheetRef.current === sheet) {
            setSheetMotionState(sheet, null);
          }
        });
      }
    },
    [enabled, expandable, dismissWhenExpanded, runDismiss, setExpanded],
  );

  const onPointerUp = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!enabled) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* already released */
      }
      finishDrag(e.clientY);
    },
    [enabled, finishDrag],
  );

  const onPointerCancel = React.useCallback(() => {
    if (!enabled) return;
    dragging.current = false;
    const sheet = sheetRef.current;
    if (!sheet) return;
    clearSheetDragTransform(sheet);
    setSheetMotionState(sheet, null);
  }, [enabled]);

  /**
   * Full-bleed listing sheets (För dig etc.): overlay the pill — never reserve a
   * fat in-flow strip above the gallery.
   */
  const overlayDragHandle = dismissFromFullSheet || dismissWhenExpanded;

  /**
   * Fullscreen lightbox: swipe zone below status bar, wide center band (chevrons stay on sides).
   */
  const fullscreenDismissZone =
    enabled && dismissFromFullSheet ? (
      <div
        className="native-lightbox-dismiss-zone absolute left-14 right-14 bottom-[12dvh] z-10 cursor-grab active:cursor-grabbing touch-none pointer-events-auto"
        style={{
          top: "calc(var(--native-safe-top, env(safe-area-inset-top, 0px)) + 3.25rem)",
        }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
        role="presentation"
        aria-hidden
      />
    ) : null;

  // ~40px touch strip, ~4px pill — easy to grab, not a fat white band.
  const dragHandle = enabled ? (
    <div
      className={cn(
        "flex w-full shrink-0 justify-center items-start cursor-grab active:cursor-grabbing select-none touch-none pt-2.5 pb-3",
        overlayDragHandle
          ? cn(
              "absolute inset-x-0 left-0 right-0 z-30 pointer-events-auto",
              dismissFromFullSheet
                ? "native-lightbox-drag-handle"
                : "native-sheet-drag-zone",
            )
          : "native-sheet-drag-zone",
      )}
      style={
        overlayDragHandle
          ? {
              // Lightbox may sit under the status bar; expanded sheets already
              // clear --native-safe-top — keep pill flush to sheet top.
              top: dismissFromFullSheet
                ? "calc(var(--native-safe-top, env(safe-area-inset-top, 0px)) + 0.35rem)"
                : "0",
              left: 0,
              right: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }
          : undefined
      }
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      role="presentation"
      aria-hidden
    >
      <div
        className="mx-auto w-12 shrink-0 rounded-full bg-muted-foreground/55"
        style={{ height: 4, minHeight: 4 }}
      />
    </div>
  ) : null;

  const backdropPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      if (!enabled || dragging.current || dismissing.current) return;
      if (e.target === e.currentTarget) runDismiss(0);
    },
    [enabled, runDismiss],
  );

  const blockRadixDismiss = React.useCallback((e: Event) => {
    // Native sheets dismiss via swipe/backdrop — block Radix outside-interact close
    // so controlled `open` does not desync (e.g. tab switches inside FeedbackDialog).
    e.preventDefault();
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      expandedRef.current = false;
      return;
    }
    if (sheetOpen) {
      if (expandable && nativeSheetExpandPreference.get()) {
        const sheet = sheetRef.current;
        expandedRef.current = true;
        if (sheet) {
          sheet.classList.add("native-sheet-expanded");
        }
        syncNativeSheetExpandState(true);
      }
      return;
    }

    const timer = window.setTimeout(() => {
      if (countOpenNativeSheets() === 0) {
        nativeSheetExpandPreference.reset();
      }
      resetSheetExpandState(sheetRef.current, expandedRef);
      dragging.current = false;
      dismissing.current = false;
    }, 0);

    return () => clearTimeout(timer);
  }, [enabled, sheetOpen, expandable]);

  return {
    mergeSheetRef,
    dragHandle,
    fullscreenDismissZone,
    backdropPointerDown,
    blockRadixDismiss,
  };
}
